import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { fmtK } from '@/features/bi/shared/format';
import { IconChart, IconDollar, IconReceivable, IconPayable, IconUsers, IconProducts, IconWarehouse, IconTrendUp, IconCalendar, IconFactory, IconSearch, IconClock } from '@/components/ui/Icon';
import { Truncate } from '@/components/ui/Truncate';
import { DeltaChip } from '@/components/ui/DeltaChip';
import { homeEntranceActive } from '../homeEntrance';
import { useReducedMotion } from '@/shared/hooks/useReducedMotion';
import { registerWidget, type WidgetRenderProps, type WidgetConfigProps } from '../registry';
import { deltaMeta, type Metric } from './metrics';

// Every tile-suitable KPI in the system: the core home metrics plus all
// Analytics/BI single-value metrics (values come from /home/metrics; this map
// carries the display label, group and RBAC node). Add a row when a new metric
// is added to src/services/home-kpi-catalog.js, with the label it has there:
// the tile shows this map's label before the server's.
export type KpiGroup = 'Finance' | 'Sales' | 'Purchasing' | 'Customers' | 'Products' | 'Inventory' | 'Growth' | 'Projects' | 'Operations';
type KpiDef = { label: string; group: KpiGroup; node: string };

// Per-group accent + icon (kept per-group rather than per-metric so the ~90
// metrics stay maintainable).
const GROUP_STYLE: Record<KpiGroup, { accent: string; icon: ReactNode }> = {
  Finance:    { accent: '#059669', icon: <IconDollar size={16} /> },
  Sales:      { accent: '#2563eb', icon: <IconReceivable size={16} /> },
  Purchasing: { accent: '#d97706', icon: <IconPayable size={16} /> },
  Customers:  { accent: '#0891b2', icon: <IconUsers size={16} /> },
  Products:   { accent: '#0e7490', icon: <IconProducts size={16} /> },
  Inventory:  { accent: '#0d9488', icon: <IconWarehouse size={16} /> },
  Growth:     { accent: '#1f8ba3', icon: <IconTrendUp size={16} /> },
  Projects:   { accent: '#db2777', icon: <IconCalendar size={16} /> },
  Operations: { accent: '#ea580c', icon: <IconFactory size={16} /> },
};
const GROUP_ORDER: KpiGroup[] = ['Finance', 'Sales', 'Purchasing', 'Customers', 'Products', 'Inventory', 'Growth', 'Projects', 'Operations'];

const KPI_META: Record<string, KpiDef> = {
  "cash_on_hand": { label: "Cash on hand", group: "Finance", node: "ledger.profit_loss" },
  "mtd_revenue": { label: "Income MTD", group: "Finance", node: "ledger.profit_loss" },
  "mtd_expenses": { label: "Expenses MTD", group: "Finance", node: "ledger.profit_loss" },
  "net_income": { label: "Net income MTD", group: "Finance", node: "ledger.profit_loss" },
  "total_ar": { label: "Accounts receivable", group: "Finance", node: "receivables.invoices" },
  "total_ap": { label: "Accounts payable", group: "Finance", node: "payables.bills" },
  "ar_overdue": { label: "AR overdue", group: "Finance", node: "receivables.invoices" },
  "ap_due": { label: "AP overdue", group: "Finance", node: "payables.bills" },
  "gross_margin_pct": { label: "Gross margin MTD", group: "Finance", node: "ledger.profit_loss" },
  "inventory_value": { label: "Inventory value", group: "Inventory", node: "inventory.on_hand" },
  "open_so_value": { label: "Open SO pipeline", group: "Sales", node: "sales.orders" },
  "open_po_value": { label: "Open PO commitment", group: "Purchasing", node: "payables.purchase_orders" },
  "vendor_ytd_spend": { label: "Vendor spend YTD", group: "Purchasing", node: "payables.vendors" },
  "open_sales_orders": { label: "Open sales orders", group: "Sales", node: "sales.orders" },
  "open_purchase_orders": { label: "Open purchase orders", group: "Purchasing", node: "payables.purchase_orders" },
  "open_purchase_requisitions": { label: "Open requisitions", group: "Purchasing", node: "payables.purchase_requisitions" },
  "open_manufacturing_orders": { label: "Open production orders", group: "Operations", node: "production.manufacturing_orders" },
  "active_customers": { label: "Active customers", group: "Customers", node: "sales.customers" },
  "active_vendors": { label: "Active vendors", group: "Purchasing", node: "payables.vendors" },
  "active_items": { label: "Active items", group: "Products", node: "products.items" },
  "revenue_ytd": { label: "Revenue YTD", group: "Finance", node: "ledger.profit_loss" },
  "expenses_ytd": { label: "Expenses YTD", group: "Finance", node: "ledger.profit_loss" },
  "net_income_ytd": { label: "Net income YTD", group: "Finance", node: "ledger.profit_loss" },
  "net_margin_ytd": { label: "Net margin YTD", group: "Finance", node: "ledger.profit_loss" },
  "revenue_last_12m": { label: "Revenue (last 12 mo)", group: "Finance", node: "ledger.profit_loss" },
  "expenses_last_12m": { label: "Expenses (last 12 mo)", group: "Finance", node: "ledger.profit_loss" },
  "net_income_last_12m": { label: "Net income (last 12 mo)", group: "Finance", node: "ledger.profit_loss" },
  "ar_current": { label: "AR not yet due", group: "Finance", node: "ledger.profit_loss" },
  "ar_aging_1_30": { label: "AR overdue 1-30 days", group: "Finance", node: "ledger.profit_loss" },
  "ar_aging_31_60": { label: "AR overdue 31-60 days", group: "Finance", node: "ledger.profit_loss" },
  "ar_aging_61_90": { label: "AR overdue 61-90 days", group: "Finance", node: "ledger.profit_loss" },
  "ar_aging_over_90": { label: "AR overdue 90+ days", group: "Finance", node: "ledger.profit_loss" },
  "ap_current": { label: "AP not yet due", group: "Finance", node: "ledger.profit_loss" },
  "ap_aging_over_90": { label: "AP overdue 90+ days", group: "Finance", node: "ledger.profit_loss" },
  "net_working_capital": { label: "Net AR - AP", group: "Finance", node: "ledger.profit_loss" },
  "dso_days": { label: "DSO (days)", group: "Finance", node: "ledger.profit_loss" },
  "dpo_days": { label: "DPO (days)", group: "Finance", node: "ledger.profit_loss" },
  "ar_overdue_invoices": { label: "Overdue AR invoices", group: "Finance", node: "ledger.profit_loss" },
  "ap_overdue_invoices": { label: "Overdue AP invoices", group: "Finance", node: "ledger.profit_loss" },
  "sales_bookings_ytd": { label: "Sales bookings YTD", group: "Sales", node: "sales.orders" },
  "sales_bookings_mtd": { label: "Sales bookings MTD", group: "Sales", node: "sales.orders" },
  "sales_order_count_ytd": { label: "Sales orders YTD", group: "Sales", node: "sales.orders" },
  "avg_order_value_ytd": { label: "Avg order value YTD", group: "Sales", node: "sales.orders" },
  "open_quotes": { label: "Open quotes", group: "Sales", node: "sales.orders" },
  "open_quotes_value": { label: "Open quotes value", group: "Sales", node: "sales.orders" },
  "quote_win_rate_ytd": { label: "Quote win rate YTD", group: "Sales", node: "sales.orders" },
  "invoiced_sales_ytd": { label: "Invoiced sales YTD", group: "Sales", node: "sales.customers" },
  "invoiced_sales_mtd": { label: "Invoiced sales MTD", group: "Sales", node: "sales.customers" },
  "invoice_count_ytd": { label: "Invoices issued YTD", group: "Sales", node: "sales.customers" },
  "avg_invoice_value_ytd": { label: "Avg invoice value YTD", group: "Sales", node: "sales.customers" },
  "first_time_buyers_ytd": { label: "First-time buyers YTD", group: "Sales", node: "sales.customers" },
  "purchases_ytd": { label: "Purchases YTD", group: "Purchasing", node: "payables.purchase_orders" },
  "po_count_ytd": { label: "Purchase orders YTD", group: "Purchasing", node: "payables.purchase_orders" },
  "avg_po_value_ytd": { label: "Avg PO value YTD", group: "Purchasing", node: "payables.purchase_orders" },
  "po_received_pct_ytd": { label: "PO received rate YTD", group: "Purchasing", node: "payables.purchase_orders" },
  "pending_po_count": { label: "POs pending delivery YTD", group: "Purchasing", node: "payables.purchase_orders" },
  "overdue_po_count": { label: "Overdue POs", group: "Purchasing", node: "payables.purchase_orders" },
  "uninvoiced_receipts_value": { label: "Uninvoiced receipts", group: "Purchasing", node: "payables.purchase_orders" },
  "purchasing_vendors_ytd": { label: "Vendors purchased from YTD", group: "Purchasing", node: "payables.purchase_orders" },
  "vendor_bills_ytd": { label: "Vendor bills posted YTD", group: "Purchasing", node: "payables.vendors" },
  "new_customers_ytd": { label: "New customers YTD", group: "Customers", node: "sales.customers" },
  "buying_customers_ytd": { label: "Customers billed YTD", group: "Customers", node: "sales.customers" },
  "revenue_per_customer_ytd": { label: "Avg revenue per customer YTD", group: "Customers", node: "sales.customers" },
  "top_customer_revenue_share_pct_ytd": { label: "Top customer revenue share YTD", group: "Customers", node: "sales.customers" },
  "top3_customer_concentration_pct_ytd": { label: "Top-3 customer concentration YTD", group: "Customers", node: "sales.customers" },
  "repeat_customers_ytd": { label: "Repeat customers YTD", group: "Customers", node: "sales.customers" },
  "avg_invoices_per_customer_ytd": { label: "Avg invoices per customer YTD", group: "Customers", node: "sales.customers" },
  "customers_with_ar_balance": { label: "Customers with AR balance", group: "Customers", node: "sales.customers" },
  "units_sold_ytd": { label: "Units sold YTD", group: "Products", node: "products.items" },
  "product_revenue_ytd": { label: "Product revenue YTD", group: "Products", node: "products.items" },
  "out_of_stock_items": { label: "Out-of-stock items (physical)", group: "Inventory", node: "inventory.on_hand" },
  "inventory_items": { label: "Inventory items", group: "Inventory", node: "products.items" },
  "sellable_items": { label: "Sellable items", group: "Products", node: "products.items" },
  "avg_product_margin_pct": { label: "Avg product margin %", group: "Products", node: "products.items" },
  "inventory_units_on_hand": { label: "Units on hand (physical)", group: "Inventory", node: "inventory.on_hand" },
  "reserved_stock_value": { label: "Reserved stock value", group: "Inventory", node: "inventory.on_hand" },
  "available_stock_value": { label: "Available stock value", group: "Inventory", node: "inventory.on_hand" },
  "cogs_12mo": { label: "COGS last 12 months", group: "Inventory", node: "inventory.transactions" },
  "inventory_turns_12mo": { label: "Inventory turns (12mo)", group: "Inventory", node: "inventory.transactions" },
  "revenue_yoy_ytd_pct": { label: "Revenue growth YTD (YoY)", group: "Growth", node: "ledger.profit_loss" },
  "expense_yoy_ytd_pct": { label: "Expense growth YTD (YoY)", group: "Growth", node: "ledger.profit_loss" },
  "net_income_yoy_ytd_pct": { label: "Net income growth YTD (YoY)", group: "Growth", node: "ledger.profit_loss" },
  "revenue_mom_pct": { label: "Revenue growth MoM", group: "Growth", node: "ledger.profit_loss" },
  "net_income_mom_pct": { label: "Net income growth MoM", group: "Growth", node: "ledger.profit_loss" },
  "active_projects": { label: "Active projects", group: "Projects", node: "projects.projects" },
  "project_contract_value": { label: "Project contract value", group: "Projects", node: "projects.projects" },
  "project_committed_open": { label: "Project open commitments", group: "Projects", node: "projects.projects" },
  "project_actual_cost": { label: "Project actual cost", group: "Projects", node: "projects.projects" },
  "project_billed_total": { label: "Project billed to date", group: "Projects", node: "projects.projects" },
  "project_revenue_ytd": { label: "Project revenue YTD", group: "Projects", node: "projects.projects" },
  "unbilled_project_amount": { label: "Unbilled project amount", group: "Projects", node: "projects.projects" },
  "unbilled_project_hours": { label: "Unbilled billable hours", group: "Projects", node: "projects.projects" },
  "project_margin_pct": { label: "Project portfolio margin", group: "Projects", node: "projects.projects" },
};
const DEFAULT_METRIC = 'cash_on_hand';
// Icon chip in the theme accent, the same on every tile (was a fixed violet).
const CHIP_BG = 'color-mix(in srgb, var(--primary) 10%, white)';
const CHIP_FG = 'var(--primary)';

// The icon says what kind of number it is: a percentage is a trend, a count
// in Finance (DSO/DPO days) a clock, everything else the group's own icon.
export function kpiIconFor(format: Metric['format'] | undefined, group: KpiGroup): ReactNode {
  if (format === 'percent') return <IconTrendUp size={16} />;
  if (format === 'count' && group === 'Finance') return <IconClock size={16} />;
  return GROUP_STYLE[group].icon;
}

function fmtValue(value: number, fmt: string): string {
  return fmt === 'money' ? fmtK(value) : fmt === 'percent' ? `${Number(value.toFixed(1))}%` : Math.round(value).toLocaleString();
}

// Login entrance: the number counts up to its value once per session.
function useCountUp(target: number, enabled: boolean): number {
  const [v, setV] = useState(enabled ? 0 : target);
  const ran = useRef(false);
  useEffect(() => {
    if (!enabled || ran.current || !target) { setV(target); return; }
    ran.current = true;
    const t0 = performance.now(); const dur = 900;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min((t - t0) / dur, 1);
      setV(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, enabled]);
  // Disabled (reduced motion, entrance over): the value itself, never a stale
  // 0 painted for a frame before the effect catches up.
  return enabled ? v : target;
}

export function KpiRender({ config, w }: WidgetRenderProps) {
  const metricKey = String(config.metric ?? DEFAULT_METRIC);
  const q = useQuery({ queryKey: ['home-metrics'], queryFn: () => api.get<Metric[]>('/home/metrics') });
  const m = q.data?.find((x) => x.key === metricKey);
  const value = m ? m.value : 0;
  const reduced = useReducedMotion();
  const shown = useCountUp(Number(value) || 0, homeEntranceActive() && !reduced);
  const d = deltaMeta(metricKey, m?.delta_pct ?? null);
  const val = fmtValue(shown, m?.format ?? 'money');
  const def = KPI_META[metricKey];
  const group: KpiGroup = def?.group ?? 'Finance';
  const icon = kpiIconFor(m?.format, group);
  const label = def?.label ?? m?.label ?? metricKey;

  if ((w ?? 2) <= 1) {
    // 1x1: title on top, icon in the middle, value below — all centered.
    return (
      <div className="flex flex-col h-full items-center justify-center gap-0.5 min-w-0 text-center">
        <Truncate as="div" text={label} className="w-full text-[0.625rem] font-medium uppercase tracking-[0.01em] text-primary-text leading-none" />
        <span aria-hidden="true" className="shrink-0 w-7 h-7 rounded-lg inline-flex items-center justify-center [&_svg]:w-3.5 [&_svg]:h-3.5"
          style={{ background: CHIP_BG, color: CHIP_FG }}>{icon}</span>
        <div className="w-full text-[0.8125rem] font-semibold text-primary-text tabular-nums truncate leading-none">{val}</div>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center gap-2.5 min-w-0 pl-0.5">
      <span aria-hidden="true" className="shrink-0 w-9 h-9 rounded-xl inline-flex items-center justify-center [&_svg]:w-[18px] [&_svg]:h-[18px]"
        style={{ background: CHIP_BG, color: CHIP_FG }}>{icon}</span>
      <div className="min-w-0 flex flex-col justify-center">
        <Truncate as="div" text={label} className="text-[0.625rem] font-medium uppercase tracking-[0.01em] text-primary-text leading-tight" />
        <div className="flex items-baseline gap-1.5 min-w-0 leading-tight">
          <span className="text-[1.125rem] font-semibold text-primary-text tabular-nums truncate leading-none">{val}</span>
          {m?.delta_pct != null && <DeltaChip value={m.delta_pct} favorable={d?.favorable} context="vs previous period" className="shrink-0" />}
        </div>
      </div>
    </div>
  );
}

// Searchable, grouped metric picker: type to filter across all ~90 KPIs.
function KpiConfig({ config, onChange }: WidgetConfigProps) {
  const [search, setSearch] = useState('');
  const current = String(config.metric ?? DEFAULT_METRIC);
  const q = search.trim().toLowerCase();
  const entries = Object.entries(KPI_META) as [string, KpiDef][];
  const filtered = q ? entries.filter(([, d]) => d.label.toLowerCase().includes(q) || d.group.toLowerCase().includes(q)) : entries;
  const byGroup = GROUP_ORDER
    .map((g) => ({ group: g, items: filtered.filter(([, d]) => d.group === g) }))
    .filter((x) => x.items.length > 0);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted"><IconSearch size={13} /></span>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search KPIs…" autoFocus
          className="w-full h-[28px] pl-7 pr-2 rounded-md border border-border bg-surface text-[0.8125rem]" />
      </div>
      <div className="max-h-56 overflow-auto rounded-md border border-border-light">
        {byGroup.length === 0 && <div className="px-2 py-2 text-[0.75rem] text-muted">No KPIs match.</div>}
        {byGroup.map(({ group, items }) => (
          <div key={group}>
            <div className="sticky top-0 bg-canvas-soft px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide text-muted">{group}</div>
            {items.map(([k, d]) => (
              <button key={k} type="button" onClick={() => onChange({ ...config, metric: k })}
                className={`w-full flex items-center gap-2 px-2 py-1 text-left text-[0.8125rem] hover:bg-row-highlight ${k === current ? 'bg-primary/10 text-primary font-medium' : 'text-primary-text'}`}>
                <span className="shrink-0 [&_svg]:w-3.5 [&_svg]:h-3.5" style={{ color: GROUP_STYLE[d.group].accent }}>{GROUP_STYLE[d.group].icon}</span>
                <span className="truncate">{d.label}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

registerWidget({
  type: 'kpi',
  label: 'KPI',
  defaultSize: 'S',
  compact: true,
  defaultWH: { w: 2, h: 1 },
  bounds: { minW: 1, maxW: 2, minH: 1, maxH: 1 },
  icon: <IconChart size={13} />,
  accent: '#2563eb',
  nodeFor: (c) => KPI_META[String(c.metric ?? DEFAULT_METRIC)]?.node ?? 'ledger.profit_loss',
  ConfigForm: KpiConfig,
  Render: KpiRender,
});
